import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Dialog, Modal, Typography } from '@mui/material';
import { HubThemeScope, HubButtonSizeContext, FONT_DISPLAY, FONT_BODY } from '../clients/hub/designSystem';
import { HEAD, BODY } from '../v2/kit';
import { Diamond as V2Diamond } from '../v2/Diamond';
import { Kicker as V2Kicker } from '../v2/Kicker';
import { V2Root, useV2Mode } from '../v2/V2Root';
import { HubIconButton } from './HubIconButton';

/**
 * HubModal — THE modal shell (design unification Phase 3.1, spec:
 * docs/design-audit/handoff-master.md §3.1 + the modal-sweep prototype).
 * Every edit/detail dialog migrates onto this shell (Phase 4B sweep) instead
 * of wrapping MUI Dialog ad hoc: scrim + centered paper, HubThemeScope inside
 * the portal so every field/select/menu/chip inherits the grammar, the head,
 * a content region and a HubButton footer (secondary left, the one primary right).
 *
 * Design System 2.0 (the published HubModal page; step 5 wave 1a): paper on a
 * 1px rule-strong hairline, the 6px panel corner, the modal shadow; the head is
 * the indigo kicker (the classic `crumb`) over a 20 / 800 title and an optional
 * 13.5 faint sub-line on a rule-soft hairline (the classic SheetHead retired);
 * footer buttons are the default 40 (`HubButtonSizeContext`); a confirm's
 * action is the danger button. Both voices rise on the shared `ds-dialog-rise`
 * (250ms); nothing moves under reduced motion.
 */

const WIDTHS: Record<'sm' | 'md' | 'lg' | 'drill', number> = { sm: 420, md: 640, lg: 920, drill: 1020 };

/**
 * `drill` — the DRILL-DOWN frame (added 2026-08-26).
 *
 * 1020 is the approved design's own width (`data-system/drill.jsx`, §3 "ONE
 * drill-down grammar for every number"). The S1-c3 port shipped the drill on
 * `lg` (920) and recorded the gap in DrillModal's own file header as "the one
 * width difference" — and 920 is precisely what made the evidence table
 * insist on more room than its grid track and scroll sideways with the last
 * column cut off (reported off the 2026-08-25 L10). This restores the design.
 *
 * A drill is a READ surface — an evidence table beside a trend — not a form,
 * so 06-design-system.md §4.7's "lg:920 is the boundary between form and
 * detail" is untouched: that boundary still governs FORMS. Like `xl`, this is
 * a closed preset rather than an open `width` prop precisely so it cannot
 * become a general-purpose escape hatch from it. Unlike `xl` it is a single
 * scalar and fixes no height — the paper still grows to fit its content.
 */

/**
 * `xl` — the WORKSPACE frame (added 2026-08-04 for the Build Block Scheduler).
 *
 * The three form sizes are single scalars that let the paper grow to fit its
 * content. `xl` is deliberately different on both axes because it serves a
 * different kind of surface: a multi-region layout — a variable-width day grid
 * beside a persistent selection panel — which has to FILL a known box rather
 * than grow to fit one. That needs a fixed height (so the grid can scroll
 * inside it) and a real width at each breakpoint (so the panel can fold into a
 * bottom sheet and then into a drill-down).
 *
 * The breakpoints are the design's own — 768 / 1024, the PublicBookingPage
 * geometry — not MUI's md/lg (900/1200), so they are written as explicit media
 * queries rather than sx breakpoint keys. Widths 412 / 900 / 1080 and heights
 * calc(100vh - 40px) / 700 / 740 come from the approved specimen.
 *
 * This widens `06-design-system.md` §4.7's "lg:920 is the boundary between
 * form and detail" — the boundary still holds for FORMS; `xl` is a third thing,
 * and it is a preset rather than an open `width` prop precisely so it cannot
 * become a general-purpose escape hatch from that rule.
 */
const XL_FRAME = {
  width: 412,
  // Mobile fills the dialog container rather than doing viewport math: the
  // paper's parent (.MuiDialog-container) is already window-height, and MUI
  // caps the paper at calc(100% - 64px), so `100%` yields a full-height sheet
  // with no magic offset. THE HEIGHT GATE bans `height: calc(100vh - N)`
  // outright — only `max-height` may measure against the window — and the
  // design ratchet enforces it, which is how the first draft of this was caught.
  height: '100%',
  '@media (min-width:768px)': { width: 900, height: 700 },
  '@media (min-width:1024px)': { width: 1080, height: 740 },
};

export interface HubModalClassicProps {
  open: boolean;
  onClose: () => void;
  title: string;
  crumb?: string;
  size?: 'sm' | 'md' | 'lg' | 'drill' | 'xl';
  /** Workspace mode: the body gets NO padding and does NOT scroll, because the
   *  surface owns its own regions and its own scroll port. Pairs with
   *  `size="xl"` and a custom `header`. Leave off for ordinary form modals —
   *  their padded, scrolling body is the whole point. */
  flush?: boolean;
  /** Footer region — compose HubButtons (ghost left, primary right). */
  footer?: React.ReactNode;
  /** When set, content + footer render inside a <form> so Enter submits. */
  onSubmit?: (e: React.FormEvent) => void;
  /** Keep the dialog up on scrim clicks (Escape still closes). */
  disableOutsideClose?: boolean;
  hideClose?: boolean;
  /** Replaces the default SheetHead+close header region (e.g. the confirm
   *  high-stakes header). When set, `title`/`crumb`/`hideClose` are ignored. */
  header?: React.ReactNode;
  /** A thin accent bar across the very top of the paper (e.g. paprika for a
   *  high-stakes confirm). Pass a CSS color / token. */
  topTicker?: string;
  children: React.ReactNode;
  /** Classic voice: leave unset. */
  voice?: undefined;
}

/**
 * Portal V2 voice (opt-in via `voice: 'v2'`; without it the render is
 * byte-identical). The design's `Modal` (kit.jsx:241-252): a scrim of
 * rgba(22,24,27,.44) fading in (`v2-dnDim`), the panel rising 10px from .985
 * over 200ms (`v2-hubDialogRise`) on paper with a 1px rule-strong frame, radius
 * 6 and the `0 28px 70px -20px rgba(0,0,0,.45)` shadow. Widths sm 420 · md 640 ·
 * lg 920 · drill 1020 under a 56px top inset; `xl` fills the window inside a 24px
 * inset; `phone` is full screen with square corners. Escape and a press on the
 * scrim close it. The panel is a bare flex column — compose `HubModalHead`,
 * `HubModalBody` and `HubModalFoot` inside. The portaled content re-wraps in
 * `V2Root` with the caller's mode so the V2 classes and night tokens reach it.
 * Inside a `HubOverlayHost` it pins to the host at the design's z-index 500,
 * as the design pins dialogs to the shell viewport (shell.jsx:151); with no
 * host it covers the window on the app's modal layer.
 */
export interface HubModalV2Props {
  voice: 'v2';
  open: boolean;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg' | 'drill' | 'xl';
  /** The design's `bp.phone`: full screen, square corners. */
  phoneFullScreen?: boolean;
  /** Pin to this element instead of the nearest `HubOverlayHost` (or the window). */
  container?: HTMLElement | null;
  /** Keep the dialog up on scrim presses (Escape still closes). */
  disableOutsideClose?: boolean;
  ariaLabel?: string;
  children: React.ReactNode;
}

export type HubModalProps = HubModalClassicProps | HubModalV2Props;

const HubModalClassic: React.FC<HubModalClassicProps> = ({ open, onClose, title, crumb, size = 'sm', flush, footer, onSubmit, disableOutsideClose, hideClose, header, topTicker, children }) => {
  /**
   * The paper is a flex column: header pinned, CONTENT SCROLLS, footer pinned.
   *
   * Before 2026-08-03 the paper was `overflow:hidden` with no scrolling region,
   * so any modal taller than the viewport simply CLIPPED — and because MUI caps
   * the paper at `calc(100% - 64px)`, the part that got cut off was the bottom:
   * the footer, i.e. the buttons. It presented as "expanding a group doesn't
   * expand the modal, things get cut off" and "zoomed in, I couldn't see the
   * Add win button" — the same defect, since zooming shrinks the effective
   * viewport and makes the cap bite sooner. Filed 2026-07-20 off CD-22, which
   * worked around it locally with its own `maxHeight:56vh`; this is the fix at
   * the shell so every modal gets it.
   *
   * `minHeight: 0` on the scroll region is what actually lets it shrink — a
   * flex item's automatic minimum is its content size, so without it the region
   * refuses to be smaller than its content and the clipping comes straight back.
   */
  const body = (
    <>
      <Box sx={flush
        // Workspace: no padding, no scroll HERE — the surface's own regions do
        // both. It still has to be a flex column with minHeight:0 or the
        // regions inside it cannot shrink and the paper overflows (THE HEIGHT
        // GATE — a flex item's automatic minimum is its content size).
        ? { flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
        : { px: 2.75, pt: header ? 0 : 2, pb: footer ? 0 : 2.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
        {children}
      </Box>
      {footer && (
        <Box sx={{
          flexShrink: 0,
          display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1,
          px: 2.75, pt: 2, pb: 2.5,
        }}>
          {/* 2.0: footer buttons are the default 40 (13.5). */}
          <HubButtonSizeContext.Provider value="md">{footer}</HubButtonSizeContext.Provider>
        </Box>
      )}
    </>
  );
  return (
    <HubThemeScope>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (disableOutsideClose && reason === 'backdropClick') return;
          onClose();
        }}
        maxWidth={false}
        // A record window or the email composer opened from here is portaled to
        // <body>, outside this dialog: never take the caret back from it (PQR-005).
        disableEnforceFocus
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(22,24,27,.44)' } } }}
        PaperProps={{
          sx: {
            ...(size === 'xl' ? XL_FRAME : { width: WIDTHS[size] }),
            maxWidth: '94vw', m: 2,
            // Design System 2.0 (the published HubModal page): paper, a 1px
            // rule-strong hairline, the 6px panel corner, the modal shadow.
            bgcolor: 'var(--paper)', backgroundImage: 'none', color: 'var(--ink)',
            border: '1px solid var(--rule-strong)', borderRadius: '6px', boxShadow: 'var(--modal)',
            overflow: 'hidden',
            // Flex column so the content region below can own the scrolling.
            // MUI already caps the paper at calc(100% - 64px); the cap was never
            // the problem — the absence of anywhere for the overflow to GO was.
            display: 'flex', flexDirection: 'column',
          },
        }}
      >
        {topTicker && <Box sx={{ height: 3, bgcolor: topTicker, flexShrink: 0 }} />}
        {header ? <Box sx={{ flexShrink: 0 }}>{header}</Box> : (
          // 2.0 head: the crumb is the indigo kicker naming where you are, over
          // the 20 / 800 title, on a rule-soft hairline (the classic 21px title
          // over a 2px ink rule retired with SheetHead).
          <HubModalHead kicker={crumb || undefined} title={title} onClose={hideClose ? undefined : onClose} />
        )}
        {/* The form has to be a flex column too, otherwise it is an ordinary
            block between the paper and the content region and the scroll
            plumbing stops one level short. */}
        {onSubmit ? (
          <Box
            component="form"
            onSubmit={onSubmit}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}
          >
            {body}
          </Box>
        ) : body}
      </Dialog>
    </HubThemeScope>
  );
};

/**
 * HubOverlayHost — the positioning context V2 overlays pin to. The design's
 * shell says it outright (shell.jsx:151): "drawers, dialogs and the bulk bar
 * pin to the shell viewport" — the row holding the rail and the page, below the
 * staff band and the phone bar. Render the shell's rail + page row as a
 * `HubOverlayHost`; a V2 `HubModal` or `HubRowDrawer` inside it portals into
 * that element and covers exactly it. Outside any host they cover the window.
 */
/* null = no host; { el: null } = a host that has not mounted yet. */
const HubOverlayHostContext = createContext<{ el: HTMLElement | null } | null>(null);

/** The nearest host element (null outside a host, or before the host mounts). */
export function useHubOverlayHost(): HTMLElement | null {
  return useContext(HubOverlayHostContext)?.el ?? null;
}

export const HubOverlayHost: React.FC<{ className?: string; style?: React.CSSProperties; children?: React.ReactNode }> = ({ className, style, children }) => {
  const [el, setEl] = useState<HTMLDivElement | null>(null);
  const value = useMemo(() => ({ el }), [el]);
  return (
    <div ref={setEl} className={className} style={{ position: 'relative', ...style }}>
      <HubOverlayHostContext.Provider value={value}>{children}</HubOverlayHostContext.Provider>
    </div>
  );
};

/**
 * The frame V2 overlays share: MUI Modal for the portal, focus trap, scroll lock
 * and layer, with its stock backdrop and Escape handling off (the design draws
 * its own scrim and listens on the document). Pinned to a host it is absolute at
 * the design's z-index; unhosted it is the window-fixed modal layer.
 */
export const HubV2OverlayFrame: React.FC<{ open: boolean; container?: HTMLElement | null; zIndex: number; children: React.ReactElement }> = ({ open, container, zIndex, children }) => {
  const nearest = useContext(HubOverlayHostContext);
  const host = container === undefined ? (nearest ? nearest.el : null) : container;
  /* Inside a host that has not mounted yet, wait one commit rather than open on
     the window and move — the move would leave MUI's aria-hidden on the page. */
  const pending = container === undefined && !!nearest && !nearest.el;
  return (
    <Modal open={open && !pending} hideBackdrop disableEscapeKeyDown container={host ?? undefined} disableScrollLock={!!host}
      style={host ? { position: 'absolute', zIndex } : undefined}>
      {children}
    </Modal>
  );
};

const V2_MODAL_W = { sm: 420, md: 640, lg: 920, drill: 1020 } as const;

const HubModalV2: React.FC<HubModalV2Props> = ({ open, onClose, size = 'md', phoneFullScreen: phone, container, disableOutsideClose, ariaLabel, children }) => {
  const mode = useV2Mode();
  useEffect(() => {
    if (!open) return undefined;
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [open, onClose]);
  const xl = size === 'xl';
  return (
    <HubV2OverlayFrame open={open} container={container} zIndex={500}>
      <div tabIndex={-1} style={{ position: 'absolute', inset: 0, outline: 'none' }}>
        <V2Root mode={mode} style={{ height: '100%' }}>
          <div className="v2-dnDim" onPointerDown={(e) => { if (e.target === e.currentTarget && !disableOutsideClose) onClose(); }}
            style={{ position: 'absolute', inset: 0, background: 'rgba(22,24,27,.44)', zIndex: 500, display: 'flex', justifyContent: 'center', alignItems: xl || phone ? 'stretch' : 'flex-start', padding: phone ? 0 : xl ? 24 : '56px 24px 24px' }}>
            <div role="dialog" aria-modal="true" aria-label={ariaLabel} className="ds-dialog"
              style={{ width: xl ? '100%' : V2_MODAL_W[size], maxWidth: '100%', maxHeight: '100%', display: 'flex', flexDirection: 'column', background: 'var(--paper)', border: '1px solid var(--rule-strong)', borderRadius: phone ? 0 : 6, boxShadow: 'var(--modal)', overflow: 'hidden', animation: 'ds-dialog-rise var(--dur-lift, 250ms) var(--ease-out) both' }}>
              {children}
            </div>
          </div>
        </V2Root>
      </div>
    </HubV2OverlayFrame>
  );
};

/** HubModal — dispatches on `voice` (two component types, so a voice flip remounts). */
export const HubModal: React.FC<HubModalProps> = (props) => (
  props.voice === 'v2' ? <HubModalV2 {...props} /> : <HubModalClassic {...props} />
);

/** The modal head (kit.jsx:253-265; 2.0): the indigo kicker, a 20/800 title, a 13.5 faint sub (max 560), a `right` slot and the close button, over a rule-soft hairline. */
export const HubModalHead: React.FC<{
  kicker?: React.ReactNode;
  title?: React.ReactNode;
  sub?: React.ReactNode;
  onClose?: () => void;
  right?: React.ReactNode;
}> = ({ kicker, title, sub, onClose, right }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 20px 14px', borderBottom: '1px solid var(--rule-soft)', flex: 'none' }}>
    <div style={{ flex: 1, minWidth: 0 }}>
      {kicker && <V2Kicker>{kicker}</V2Kicker>}
      <div style={{ fontFamily: HEAD, fontSize: 20, fontWeight: 800, letterSpacing: '-.015em', color: 'var(--ink)', marginTop: kicker ? 6 : 0, lineHeight: 1.2 }}>{title}</div>
      {sub && <div style={{ fontSize: 13.5, color: 'var(--ink-faint)', marginTop: 7, lineHeight: 1.5, textWrap: 'pretty', maxWidth: 560 } as React.CSSProperties}>{sub}</div>}
    </div>
    {right}
    {onClose && <HubIconButton name="close" title="Close" size={32} onClick={onClose} style={{ border: 'none', background: 'transparent' }} />}
  </div>
);

/** V2 modal body (kit.jsx:266): pads 20 and scrolls. */
export const HubModalBody: React.FC<{ children?: React.ReactNode; style?: React.CSSProperties; pad?: number | string }> = ({ children, style, pad = 20 }) => (
  <div className="v2-scroll" style={{ padding: pad, overflowY: 'auto', flex: 1, minHeight: 0, ...style }}>{children}</div>
);

/** The modal foot (kit.jsx:267-269; 2.0): a faint 12.5 `note` on the left, the actions right (40 tall), over a rule-soft hairline. */
export const HubModalFoot: React.FC<{ children?: React.ReactNode; note?: React.ReactNode }> = ({ children, note }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 20px 16px', borderTop: '1px solid var(--rule-soft)', flex: 'none', flexWrap: 'wrap' }}>
    {note && <span style={{ fontSize: 12.5, color: 'var(--ink-faint)', lineHeight: 1.4, flex: '1 1 120px' }}>{note}</span>}
    <span style={{ flex: 1 }} />
    <HubButtonSizeContext.Provider value="md">{children}</HubButtonSizeContext.Provider>
  </div>
);

/**
 * HubField — the swept-dialog field pattern (definition-of-done #3): the label
 * ABOVE the control (not MUI's floating notched label). Wrap any control; pair
 * with HubTextField for plain inputs. Design System 2.0 (D-007, D-017 B4): the
 * label is sentence case, Inter Tight 12.5 / 700 in ink (it was the uppercase
 * micro voice); the hint is 11 in ink-faint.
 */
export const HubField: React.FC<{
  label: string;
  helperText?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, helperText, children }) => (
  <Box sx={{ mt: 1.9 }}>
    <Typography sx={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 12.5, lineHeight: 1.3, color: 'var(--ink)', mb: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {label}
    </Typography>
    {children}
    {helperText && (
      <Typography sx={{ fontSize: 11, color: 'var(--ink-faint)', mt: 0.6, fontFamily: FONT_BODY }}>{helperText}</Typography>
    )}
  </Box>
);

export interface HubTextFieldProps {
  label: string;
  value: string | number;
  /** Called with the next string value (both voices). */
  onChange?: (next: string) => void;
  type?: 'text' | 'number' | 'email' | 'password' | 'url' | 'tel' | 'search';
  placeholder?: string;
  required?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
  /** The hint under the field (the V2 design calls it `hint`). */
  helperText?: React.ReactNode;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /**
   * Portal V2 voice (opt-in, default OFF — without it the render is
   * byte-identical). The design's `Field` (kit.jsx:164-179): a sentence-case
   * 12/700 label with " · required", a 1px rule-strong frame (radius 4) whose
   * `.v2-field` focus ring is indigo + a 3px indigo-soft halo, a 13.5 input,
   * `leading`/`trailing` slots, `mono`, paper-2 when read-only or disabled,
   * `textarea` + `rows`, and an `error` that turns the label, frame and hint
   * paprika behind a 5px diamond with the `v2-qWipe` reveal. Everything below
   * is V2-only. Render it inside a `V2Root`.
   */
  voice?: 'v2';
  /** V2: the design's `onChange`, called with the raw change event. */
  onChangeEvent?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  error?: React.ReactNode;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  mono?: boolean;
  readOnly?: boolean;
  textarea?: boolean;
  rows?: number;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
}

/** Plain text/number input on the hub frame. 2.0 (D-017 B4): 40 tall, a 13.5
 *  Inter value, a 1px rule-strong frame, 4px; focus is the indigo frame with the
 *  3px indigo-soft halo; the placeholder reads ink-faint (D-043). */
const HubTextFieldClassic: React.FC<HubTextFieldProps> = ({ label, value, onChange, type = 'text', placeholder, required, autoFocus, disabled, helperText, inputProps }) => (
  <HubField label={label} helperText={helperText}>
    <Box
      component="input"
      type={type}
      value={value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange?.(e.target.value)}
      placeholder={placeholder}
      required={required}
      autoFocus={autoFocus}
      disabled={disabled}
      {...inputProps}
      sx={{
        width: '100%', boxSizing: 'border-box', height: 40,
        font: `400 13.5px/1.2 ${FONT_BODY}`, color: 'var(--ink)',
        p: '0 12px', border: '1px solid var(--rule-strong)', borderRadius: '4px',
        bgcolor: disabled ? 'var(--paper-2)' : 'var(--paper)', outline: 'none',
        transition: 'border-color 120ms cubic-bezier(.16,1,.3,1), box-shadow 120ms cubic-bezier(.16,1,.3,1)',
        '&::placeholder': { color: 'var(--ink-faint)', fontFamily: FONT_BODY, fontWeight: 400, opacity: 1 },
        '&:focus': { borderColor: 'var(--indigo)', boxShadow: 'var(--focus-field)' },
      }}
    />
  </HubField>
);

/* index.html:42-43 `.v2-field` — the focus ring, carried by the frame too so it holds outside a V2Root. */
export const HUB_V2_FIELD_SX = {
  '&:focus-within': { borderColor: 'var(--indigo) !important', boxShadow: '0 0 0 3px var(--indigo-soft)' },
  '& input, & textarea': { outline: 'none' },
} as const;

const HubTextFieldV2: React.FC<HubTextFieldProps> = ({ label, value, onChange, onChangeEvent, type = 'text', placeholder, required, autoFocus, disabled, readOnly, helperText, error, leading, trailing, mono, textarea, rows = 3, style, inputStyle, onKeyDown, inputProps }) => {
  const change = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => { onChangeEvent?.(e); onChange?.(e.target.value); };
  const control = {
    value, onChange: change, placeholder, disabled, readOnly, autoFocus, onKeyDown,
    style: { flex: 1, minWidth: 0, border: 'none', background: 'transparent', padding: '10px 11px', fontSize: 13.5, color: 'var(--ink)', fontFamily: mono ? 'ui-monospace, Menlo, monospace' : BODY, resize: textarea ? 'vertical' : undefined, lineHeight: textarea ? 1.55 : undefined, ...inputStyle } as React.CSSProperties,
  };
  return (
    <label style={{ display: 'block', ...style }}>
      {label && <span style={{ display: 'block', fontFamily: HEAD, fontSize: 12.5, fontWeight: 700, color: error ? 'var(--paprika)' : 'var(--ink)', marginBottom: 6 }}>{label}{required && <span style={{ color: 'var(--ink-faint)', fontWeight: 600 }}> · required</span>}</span>}
      <Box component="span" className="v2-field" sx={HUB_V2_FIELD_SX} style={{ display: 'flex', alignItems: textarea ? 'stretch' : 'center', minHeight: textarea ? undefined : 40, boxSizing: 'border-box', border: `1px solid ${error ? 'var(--paprika)' : 'var(--rule-strong)'}`, borderRadius: 4, background: disabled || readOnly ? 'var(--paper-2)' : 'var(--paper)', transition: 'border-color 120ms ease, box-shadow 120ms ease' }}>
        {leading && <span style={{ display: 'flex', alignItems: 'center', paddingLeft: 10, color: 'var(--ink-faint)' }}>{leading}</span>}
        {textarea
          ? <textarea rows={rows} {...control} />
          : <input {...inputProps} type={type} {...control} />}
        {trailing && <span style={{ display: 'flex', alignItems: 'center', paddingRight: 8, gap: 4 }}>{trailing}</span>}
      </Box>
      {(helperText || error) && (
        <span className={error ? 'v2-qWipe' : undefined} style={{ display: 'flex', gap: 6, alignItems: 'baseline', fontSize: 11, marginTop: 6, lineHeight: 1.45, color: error ? 'var(--paprika)' : 'var(--ink-faint)' }}>
          {error && <V2Diamond size={5} color="var(--paprika)" style={{ marginTop: 4 }} />}{error || helperText}
        </span>
      )}
    </label>
  );
};

/** HubTextField — dispatches on `voice` (two component types, so a voice flip remounts). */
export const HubTextField: React.FC<HubTextFieldProps> = (props) => (
  props.voice === 'v2' ? <HubTextFieldV2 {...props} /> : <HubTextFieldClassic {...props} />
);

export default HubModal;
